/**
 * Dark-mode migration allowlist (LOAD-BEARING).
 *
 * The global dark-theme rule `body.dark-theme { color: var(--text-primary) }`
 * (theme.css) flips INHERITED text to near-white in dark mode. That is correct for
 * screens whose backgrounds are also tokenized (dark), but on NOT-yet-migrated
 * screens — which still paint hardcoded light backgrounds — inherited light text
 * lands on light and becomes invisible.
 *
 * Stopgap: MainLayout renders the <main> for any route NOT in this allowlist inside
 * a local `data-theme="light"` context (plus the `legacy-light` class), so those
 * screens keep their normal light appearance while the app shell stays dark —
 * readable, if visibly "not yet themed." Migrated routes get no override and follow
 * the app theme.
 *
 * A route enters this allowlist ONLY after a two-tier both-modes pass (amended
 * 2026-07-06): Tier 1 = agent interaction pass (both modes + scope check + light
 * lap), Tier 2 = Dave's visual spot-check. Prod-facing live gates (G-DARK-1/2)
 * stay human-only. See docs/audits/dark-mode-deploy-gate.md. Adding a prefix here
 * without that pass re-opens the invisible-text failure for that screen.
 */
export const MIGRATED_ROUTE_PREFIXES = [
  '/quotes',
  '/customers',
  '/dashboard',
  // Slice 1A + 1B promoted 2026-08-04 after a REAL two-tier both-modes pass on the
  // seeded local stack (Tier 1 agent interaction pass + Tier 2 Dave Miller visual
  // spot-check — see dark-mode-deploy-gate.md "Allowlist promotions"). This
  // supersedes the 2026-07-25 retraction of the false 2026-07-06 records, which
  // stands as history.
  '/admin/product-variants', // Slice 1A — VariantManager
  '/admin/serial-numbers', // Slice 1A — SerialRegistry
  '/leads', // Slice 1B — pipeline/detail/oversight cluster (see overrides below)
  // Slice 1C promoted 2026-08-05 after Dave's real Tier-2 both-modes pass on the
  // seeded local stack (dark-mode-deploy-gate.md "Allowlist promotions"). The prefix
  // also captures /products/detail/:id — fully themed via the scope
  // extension of PR 153, so deliberately NO override entry for it.
  '/products', // Slice 1C — ProductManagement + ProductDetailPage
  // R1a-ui (2026-09-21): the returns policy settings screen, token-only from its first
  // line. Deliberately the narrow path, NOT '/settings': that prefix would also capture
  // /settings/profile and /settings/preferences (header-menu links that 404 today) and any
  // future settings screen before it is themed. Entered on Dave's decision with the Tier-1
  // agent pass recorded in dark-mode-deploy-gate.md; Dave's Tier-2 both-modes check is
  // owed before this ships (release frontend carries the gate).
  '/settings/returns-policy',
  // R1d (2026-09-23): the returns list and detail, token-only from their first line. ONE
  // entry: matchesPrefix below treats '/returns' as covering '/returns/42', and a literal
  // '/returns/:id' would never match a real pathname. It does NOT capture '/returns-archive'
  // — the match is on whole segments. Entered on Dave's decision with the Tier-1 agent pass
  // recorded in dark-mode-deploy-gate.md; Dave's Tier-2 both-modes check on production is
  // owed, and `release frontend` carries the gate.
  '/returns',
  // O-dark (2026-10-20, ROLLOUT-ORDERS-SHELL.md §3 D11): the Orders list, its records, the amend
  // editor and the problem panels — everything App.js routes under '/orders/*', all token-only
  // since O1b-O5; ONE entry (the prefix covers /orders/:id and /orders/:id/amend, and not
  // '/orders-archive'). The lap's three low contrasts were fixed first, in the shared popover
  // and palette CSS and the pager. Tier-1 agent pass recorded in dark-mode-deploy-gate.md; Dave's
  // Tier-2 both-modes check on production is owed before the next frontend release, which
  // carries the gate.
  '/orders'
];

// Routes that a broader MIGRATED prefix would wrongly capture but are NOT yet migrated
// (they'd render white in dark). Checked FIRST — an override always wins. Here: the
// legacy Lead Capture screen and the Leads analytics screen both live under /leads/*
// but aren't in that slice. ACTIVE (not inert) now that '/leads' is promoted —
// scope-checked in the 2026-08-04 pass: both still render light-in-dark-shell.
export const UNMIGRATED_ROUTE_OVERRIDES = ['/leads/legacy', '/leads/analytics'];

const matchesPrefix = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** True if `pathname` is (or is nested under) a migrated route prefix, and not
 *  explicitly overridden as still-unmigrated. */
export function isMigratedRoute(pathname) {
  if (!pathname) return false;
  if (UNMIGRATED_ROUTE_OVERRIDES.some((p) => matchesPrefix(pathname, p))) return false;
  return MIGRATED_ROUTE_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

/**
 * Props to spread onto the layout `<main>`. Unmigrated routes get a local light
 * theme context so their still-light surfaces stay readable in dark mode; migrated
 * routes get an empty object (inherit the app theme). Mode-independent by design:
 * forcing light-in-light mode is a visual no-op (the `legacy-light` background/color
 * override is scoped to `body.dark-theme` in theme.css), so this needs no knowledge
 * of the current theme.
 */
export function legacyMainProps(pathname) {
  if (isMigratedRoute(pathname)) return {};
  return { 'data-theme': 'light', className: 'legacy-light' };
}
